import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, 
  Loader2, 
  Check, 
  Headphones, 
  Settings, 
  FileText, 
  Shield, 
  Database,
  Sparkles,
  AlertTriangle,
  Zap,
  BarChart3,
  Clock,
  Brain,
  Package,
  Users
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { OnboardingProgress } from "@/components/Onboarding/OnboardingProgress";
import { TurnstileWidget } from "@/components/Onboarding/TurnstileWidget";
import AuthLeftPanel from "@/components/Auth/AuthLeftPanel";

// Mapeamento de ícones por código do módulo
const MODULE_ICONS: Record<string, React.ReactNode> = {
  incidents: <AlertTriangle className="h-5 w-5" />,
  requests: <Headphones className="h-5 w-5" />,
  problems: <Shield className="h-5 w-5" />,
  changes: <Settings className="h-5 w-5" />,
  asset_management: <Database className="h-5 w-5" />,
  software_management: <Package className="h-5 w-5" />,
  knowledge_base: <FileText className="h-5 w-5" />,
  reports: <BarChart3 className="h-5 w-5" />,
  forms: <FileText className="h-5 w-5" />,
  sla_management: <Clock className="h-5 w-5" />,
  ai_integrated: <Brain className="h-5 w-5" />,
};

interface ModulePrice {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  is_active: boolean;
  icon: string | null;
  display_order: number;
}

interface LicenseTier {
  id: string;
  name: string;
  min_licenses: number;
  max_licenses: number;
  is_active: boolean;
  display_order: number;
}

export default function PlanSelection() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [isAnnual, setIsAnnual] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [licenseQuantity, setLicenseQuantity] = useState<number>(1);

  // Buscar módulos do banco de dados
  const { data: modules = [], isLoading: isLoadingModules } = useQuery({
    queryKey: ["module_prices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_prices")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as ModulePrice[];
    },
  });

  // Buscar tiers de licença do banco de dados
  const { data: licenseTiers = [], isLoading: isLoadingTiers } = useQuery({
    queryKey: ["license_tiers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("license_tiers")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as LicenseTier[];
    },
  });

  // Verificar autenticação
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      if (!session.user.email_confirmed_at) {
        navigate("/verify-email");
        return;
      }

      // Verificar etapa do onboarding
      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_step, client_id")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        if (profile.onboarding_step === "completed") {
          navigate("/portal");
          return;
        }

        if (!profile.client_id) {
          navigate("/onboarding/company");
          return;
        }

        setClientId(profile.client_id);
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  // Definir tier padrão quando carregar
  useEffect(() => {
    if (licenseTiers.length > 0 && !selectedTierId) {
      const defaultTier = licenseTiers[0];
      setSelectedTierId(defaultTier.id);
      setLicenseQuantity(defaultTier.max_licenses);
    }
  }, [licenseTiers, selectedTierId]);

  const handleModuleToggle = (code: string) => {
    setSelectedModules((prev) =>
      prev.includes(code)
        ? prev.filter((m) => m !== code)
        : [...prev, code]
    );
  };

  const handleTierChange = (tierId: string) => {
    setSelectedTierId(tierId);
    const tier = licenseTiers.find(t => t.id === tierId);
    if (tier) {
      setLicenseQuantity(tier.max_licenses);
    }
  };

  const getSelectedTier = () => {
    return licenseTiers.find(t => t.id === selectedTierId);
  };

  // Calcular soma dos módulos selecionados
  const calculateModulesSum = () => {
    return selectedModules.reduce((sum, code) => {
      const module = modules.find((m) => m.code === code);
      return sum + (module?.price || 0);
    }, 0);
  };

  // Calcular total: (Soma Módulos) × (Quantidade Licenças)
  const calculateTotal = () => {
    const moduleSum = calculateModulesSum();
    const total = moduleSum * licenseQuantity;
    
    // Se anual, aplica desconto (10 meses em vez de 12 = ~17% desconto)
    return isAnnual ? total * 10 : total;
  };

  const handleStartTrial = async () => {
    if (selectedModules.length === 0) {
      toast({
        title: "Selecione módulos",
        description: "Escolha pelo menos um módulo para continuar.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user || !clientId) {
        navigate("/auth");
        return;
      }

      // Buscar plano trial
      const { data: trialPlan } = await supabase
        .from("license_plans")
        .select("id")
        .eq("name", "Trial")
        .single();

      if (!trialPlan) {
        throw new Error("Plano trial não encontrado");
      }

      // Criar licença trial
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 14);

      const { error: licenseError } = await supabase
        .from("client_licenses")
        .insert({
          client_id: clientId,
          license_plan_id: trialPlan.id,
          status: "trial",
          billing_cycle: isAnnual ? "yearly" : "monthly",
          trial_end_date: trialEndDate.toISOString(),
          start_date: new Date().toISOString(),
        });

      if (licenseError) throw licenseError;

      // Inserir módulos selecionados na tabela client_modules
      const modulesToInsert = selectedModules.map((code: string) => ({
        client_id: clientId,
        module_code: code,
        is_active: true,
        source: 'trial'
      }));

      const { error: modulesError } = await supabase
        .from('client_modules')
        .insert(modulesToInsert);

      if (modulesError) {
        console.error('[PlanSelection] Erro ao salvar módulos:', modulesError);
        // Não bloquear o fluxo, apenas logar
      }

      // Atualizar profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          onboarding_step: "completed",
        })
        .eq("id", session.user.id);

      if (profileError) throw profileError;

      // Log de auditoria
      await supabase.from("audit_logs").insert({
        user_id: session.user.id,
        event_type: "onboarding_completed",
        metadata: { 
          modules: selectedModules,
          billing_cycle: isAnnual ? "yearly" : "monthly",
          license_quantity: licenseQuantity,
          type: "trial",
        },
      });

      toast({
        title: "Trial ativado!",
        description: "Seu período de teste de 14 dias começou.",
      });

      navigate("/portal");

    } catch (error: any) {
      console.error("[PlanSelection] Erro:", error);
      toast({
        title: "Erro ao ativar trial",
        description: error.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToCheckout = async () => {
    if (selectedModules.length === 0) {
      toast({
        title: "Selecione módulos",
        description: "Escolha pelo menos um módulo para continuar.",
        variant: "destructive",
      });
      return;
    }

    if (!captchaToken) {
      toast({
        title: "Verificação necessária",
        description: "Complete a verificação de segurança.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user || !clientId) {
        navigate("/auth");
        return;
      }

      // Buscar nome do cliente
      const { data: clientData } = await supabase
        .from("clients")
        .select("name, razao_social")
        .eq("id", clientId)
        .single();

      const clientName = clientData?.razao_social || clientData?.name || "Cliente";

      // Chamar edge function para criar checkout
      const { data, error } = await supabase.functions.invoke("create-modular-checkout", {
        body: {
          selectedModules,
          licenseQuantity,
          billingCycle: isAnnual ? "yearly" : "monthly",
          clientId,
          email: session.user.email,
          clientName,
          unitAmountCents: calculateModulesSum() * 100, // Preço por licença em centavos
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("URL de checkout não retornada");
      }

    } catch (error: any) {
      console.error("[PlanSelection] Erro checkout:", error);
      toast({
        title: "Erro ao iniciar pagamento",
        description: error.message || "Tente novamente.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  if (isCheckingAuth || isLoadingModules || isLoadingTiers) {
    return (
      <div className="min-h-screen w-full flex flex-col lg:flex-row">
        <AuthLeftPanel />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const selectedTier = getSelectedTier();

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row">
      <AuthLeftPanel />
      <div 
        className="flex-1 flex items-center justify-center bg-gradient-to-tl from-amber-50 via-orange-50 to-white min-h-screen lg:min-h-0 p-4 sm:p-6 lg:p-8 overflow-y-auto"
        style={{
          backgroundImage: 'url(/logos/images/fundo_apresentacao_eleeva.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <Card className="w-full max-w-4xl border-2 border-amber-200 bg-white/95 backdrop-blur-sm shadow-2xl shadow-amber-200/20">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Escolha seus módulos</CardTitle>
            <CardDescription>
              Selecione os módulos e a quantidade de licenças
            </CardDescription>
            <div className="mt-4">
              <OnboardingProgress currentStep={3} totalSteps={4} />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Toggle Anual/Mensal */}
            <div className="flex items-center justify-center gap-4 p-4 bg-muted/50 rounded-lg">
              <span className={!isAnnual ? "font-medium" : "text-muted-foreground"}>
                Mensal
              </span>
              <Switch
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
              />
              <span className={isAnnual ? "font-medium" : "text-muted-foreground"}>
                Anual
              </span>
              {isAnnual && (
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  2 meses grátis
                </Badge>
              )}
            </div>

            {/* Lista de Módulos */}
            <div className="space-y-2">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Zap className="h-4 w-4" />
                1. Selecione os Módulos
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {modules.map((module) => {
                  const isSelected = selectedModules.includes(module.code);
                  const icon = MODULE_ICONS[module.code] || <Settings className="h-4 w-4" />;
                  const isFree = module.price === 0;
                  
                  return (
                    <div
                      key={module.code}
                      className={`cursor-pointer transition-all rounded-lg border p-3 ${
                        isSelected
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                      onClick={() => handleModuleToggle(module.code)}
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={isSelected}
                          className="pointer-events-none mt-0.5 h-4 w-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className={`p-1 rounded ${
                              isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                            }`}>
                              {icon}
                            </span>
                            <h4 className="font-medium text-sm truncate">{module.name}</h4>
                          </div>
                          <div className="text-xs">
                            {isFree ? (
                              <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs px-1.5 py-0">
                                Incluso
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">
                                R$ {module.price}/lic/mês
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Seletor de Quantidade de Licenças */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                2. Quantidade de Licenças
              </Label>
              <p className="text-sm text-muted-foreground">
                Licenças são cobradas por usuário com função diferente de "Usuário Padrão"
              </p>
              
              <div className="space-y-2">
                <Label>Quantidade de Licenças</Label>
                <Select value={selectedTierId || ""} onValueChange={handleTierChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a quantidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {licenseTiers.map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.name} ({tier.min_licenses} - {tier.max_licenses} licenças)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Cobrança: <span className="font-medium text-foreground">{licenseQuantity} licenças</span>
                </p>
              </div>
            </div>

            {/* Resumo */}
            {selectedModules.length > 0 && (
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                <h4 className="font-semibold">Resumo do Plano</h4>
                
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Módulos selecionados:</span>
                    <span>{selectedModules.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valor por licença:</span>
                    <span>R$ {calculateModulesSum().toLocaleString("pt-BR")}/mês</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quantidade de licenças:</span>
                    <span>{licenseQuantity}</span>
                  </div>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        R$ {calculateModulesSum().toLocaleString("pt-BR")} × {licenseQuantity} licenças
                      </p>
                      <p className="text-2xl font-bold">
                        R$ {calculateTotal().toLocaleString("pt-BR")}
                        <span className="text-sm font-normal text-muted-foreground">
                          /{isAnnual ? "ano" : "mês"}
                        </span>
                      </p>
                    </div>
                    {isAnnual && (
                      <Badge className="bg-green-100 text-green-700">
                        Economia de R$ {(calculateModulesSum() * licenseQuantity * 2).toLocaleString("pt-BR")}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CAPTCHA */}
            <div className="flex justify-center">
              <TurnstileWidget onVerify={setCaptchaToken} />
            </div>

            {/* Botões */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/onboarding/company")}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              
              <Button
                variant="secondary"
                onClick={handleStartTrial}
                disabled={isLoading || selectedModules.length === 0}
                className="flex-1 gap-2"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Iniciar Trial Grátis (14 dias)
              </Button>

              <Button
                onClick={handleGoToCheckout}
                disabled={isLoading || selectedModules.length === 0 || !captchaToken}
                className="flex-1 gap-2"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Assinar Agora
              </Button>
            </div>

            {/* Info Trial */}
            <p className="text-center text-sm text-muted-foreground">
              O trial de 14 dias inclui todos os módulos selecionados.
              Sem necessidade de cartão de crédito.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
